import { useEffect, useMemo, useState } from "react";
import { Bell, Pencil, HandCoins, Trash2, UserPlus } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateLocale } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export const FEE_BADGE = {
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  due: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  pending: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  exempt: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  not_applicable: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

export const FeeStatusBadge = ({ status }) => {
  const { t } = useAuth();
  return (
    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border whitespace-nowrap ${FEE_BADGE[status] || FEE_BADGE.due}`} data-testid={`fee-status-${status}`}>
      {t(`fees.status_${status}`)}
    </span>
  );
};

export const eurCents = (c) => `${(c / 100).toLocaleString("fr-FR", { minimumFractionDigits: c % 100 ? 2 : 0 })} €`;

const FEE_STATUSES = ["due", "pending", "paid", "exempt", "not_applicable"];

// Tableau global des cotisations (admin : édition des statuts ; responsable musical : lecture seule)
// Mode informatif : aucune action de paiement (ni en ligne, ni « marquer payé »)
export default function FeesPanel() {
  const { user, t, lang, group } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManageFees = ["admin", "treasurer"].includes(user?.role);
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [data, setData] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterInstr, setFilterInstr] = useState("all");
  const [editFee, setEditFee] = useState(null);
  const [editForm, setEditForm] = useState({ status: "due", amount_due: "", notes: "", instrument: "" });
  const [delFee, setDelFee] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [addForm, setAddForm] = useState({ member_id: "", amount_due: "", notes: "" });

  const loadPeriods = async () => {
    const [p, cfg] = await Promise.all([api.get("/fees/periods"), api.get("/fees/config")]);
    setPeriods(p.data);
    const current = cfg.data.current_period?.id || (p.data[0]?.id ?? "");
    setPeriodId((prev) => prev || current);
  };
  const load = async () => {
    if (!periodId) { setData(null); return; }
    const r = await api.get("/fees", { params: { period_id: periodId } });
    setData(r.data);
  };
  useEffect(() => { loadPeriods().catch(() => {}); }, []);
  useEffect(() => { load().catch(() => {}); }, [periodId]);

  const instruments = useMemo(() => [...new Set((data?.rows || []).map((r) => r.member_instrument).filter(Boolean))], [data]);
  const rows = (data?.rows || []).filter((r) =>
    (filterStatus === "all" || r.status === filterStatus) &&
    (filterInstr === "all" || r.member_instrument === filterInstr)
  );
  // Membres actifs sans cotisation sur la période courante (pour l'ajout)
  const membersWithoutFee = useMemo(() => {
    const have = new Set((data?.rows || []).map((r) => r.member_id));
    return members.filter((m) => !have.has(m.id) && m.status === "ACTIVE");
  }, [members, data]);
  const periodAmount = (() => { const p = periods.find((x) => x.id === periodId); return p ? String(p.amount_cents / 100) : ""; })();

  const remind = async () => {
    try {
      const r = await api.post("/fees/remind");
      toast.success(r.data.reminded ? t("fees.remind_done", { n: r.data.reminded }) : t("fees.remind_none"));
    } catch (e) { toast.error(e.friendly); }
  };

  const submitEdit = async () => {
    try {
      await api.put(`/fees/${editFee.id}`, {
        status: editForm.status,
        amount_due: editForm.amount_due !== "" ? Number(editForm.amount_due) : null,
        notes: editForm.notes,
      });
      if (isAdmin && editForm.instrument !== (editFee.member_instrument || "")) {
        await api.put(`/members/${editFee.member_id}`, { instrument: editForm.instrument });
      }
      toast.success(t("fees.updated"));
      setEditFee(null);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const submitDelete = async () => {
    try {
      await api.delete(`/fees/${delFee.id}`);
      toast.success(t("fees.deleted"));
      setDelFee(null);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const openAdd = async () => {
    setAddOpen(true);
    setAddForm({ member_id: "", amount_due: "", notes: "" });
    try {
      const r = await api.get("/members");
      setMembers(r.data);
    } catch (e) { toast.error(e.friendly); }
  };

  const submitAdd = async () => {
    try {
      await api.post("/fees", {
        member_id: addForm.member_id,
        period_id: periodId,
        amount_due: addForm.amount_due !== "" ? Number(addForm.amount_due) : null,
        notes: addForm.notes,
      });
      toast.success(t("fees.added"));
      setAddOpen(false);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  if (!periodId && !periods.length) return <p className="t-secondary text-sm py-6 text-center" data-testid="fees-empty">{t("fees.empty")}</p>;
  const s = data?.summary;

  return (
    <div className="space-y-4" data-testid="fees-panel">
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {[
            [s.concerned, "fees.summary_concerned", "fees-summary-concerned"],
            [s.paid, "fees.summary_paid", "fees-summary-paid"],
            [s.due, "fees.summary_due", "fees-summary-due"],
            [s.exempt + s.not_applicable, "fees.summary_exempt", "fees-summary-exempt"],
            [eurCents(s.expected_cents), "fees.expected", "fees-summary-expected"],
            [eurCents(s.received_cents), "fees.received", "fees-summary-received"],
            [eurCents(s.remaining_cents), "fees.remaining", "fees-summary-remaining"],
          ].map(([v, k, tid]) => (
            <div key={tid} className="app-card p-3 text-center" data-testid={tid}>
              <p className="font-heading font-bold text-lg">{v}</p>
              <p className="text-[10px] uppercase tracking-wider t-secondary">{t(k)}</p>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs t-secondary italic" data-testid="fees-readonly-note">{t("fees.readonly_note")}</p>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={periodId} onValueChange={setPeriodId}>
          <SelectTrigger className="h-9 w-56 bg-transparent border-app" data-testid="fees-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>{periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-9 w-44 bg-transparent border-app" data-testid="fees-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("fees.filter_all_status")}</SelectItem>
            {FEE_STATUSES.map((st) => <SelectItem key={st} value={st}>{t(`fees.status_${st}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterInstr} onValueChange={setFilterInstr}>
          <SelectTrigger className="h-9 w-44 bg-transparent border-app" data-testid="fees-filter-instrument"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("fees.filter_all_instruments")}</SelectItem>
            {instruments.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
        {canManageFees && (
          <>
            <Button size="sm" variant="outline" className="border-app h-9 ml-auto" onClick={openAdd} data-testid="fees-add-button">
              <UserPlus className="h-4 w-4 mr-1" /> {t("fees.add_cotisation")}
            </Button>
            <Button size="sm" variant="outline" className="border-app h-9" onClick={remind} data-testid="fees-remind-button">
              <Bell className="h-4 w-4 mr-1" /> {t("fees.remind")}
            </Button>
          </>
        )}
      </div>

      <div className="app-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left t-secondary border-b border-app text-xs uppercase tracking-wider">
              <th className="p-3">{t("pres.member")}</th>
              <th className="p-3">{t("fees.amount_due")}</th>
              <th className="p-3">{t("fees.status_label")}</th>
              <th className="p-3">{t("fees.paid_at")}</th>
              {canManageFees && <th className="p-3 text-right">{t("usr.col_actions")}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-app/50" data-testid={`fee-row-${r.id}`}>
                <td className="p-3 font-medium">{r.member_name}</td>
                <td className="p-3">{r.status === "not_applicable" ? "—" : eurCents(r.amount_due_cents)}</td>
                <td className="p-3"><FeeStatusBadge status={r.status} /></td>
                <td className="p-3 t-secondary text-xs">
                  {r.paid_at ? t("fees.paid_on", { date: new Date(r.paid_at).toLocaleDateString(dateLocale(lang)) }) : "—"}
                  {r.payment_method && r.status === "paid" && <span className="block">{t(`fees.method_${r.payment_method}`)}</span>}
                  {r.notes && <span className="block italic">{r.notes}</span>}
                </td>
                {canManageFees && (
                  <td className="p-3">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setEditFee(r); setEditForm({ status: r.status, amount_due: String(r.amount_due_cents / 100), notes: r.notes || "", instrument: r.member_instrument || "" }); }} data-testid={`fee-edit-${r.id}`} title={t("fees.edit_title")}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-400 hover:bg-rose-500/10" onClick={() => setDelFee(r)} data-testid={`fee-delete-${r.id}`} title={t("common.delete")}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editFee} onOpenChange={() => setEditFee(null)}>
        <DialogContent className="card-bg border-app" data-testid="fee-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("fees.edit_title")} — {editFee?.member_name}</DialogTitle>
            <DialogDescription className="t-secondary">{editFee?.period_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("fees.status_label")}</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="fee-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FEE_STATUSES.map((st) => <SelectItem key={st} value={st}>{t(`fees.status_${st}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("fees.amount_due")}</Label>
              <Input type="number" min="0" step="0.01" value={editForm.amount_due} onChange={(e) => setEditForm({ ...editForm, amount_due: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="fee-amountdue-input" />
            </div>
            {isAdmin && (
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("fees.instrument_of")}</Label>
              {(group?.instruments || []).length > 0 ? (
                <Select value={editForm.instrument} onValueChange={(v) => setEditForm({ ...editForm, instrument: v })}>
                  <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="fee-instrument-select"><SelectValue placeholder={t("usr.choose")} /></SelectTrigger>
                  <SelectContent>{group.instruments.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input value={editForm.instrument} onChange={(e) => setEditForm({ ...editForm, instrument: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="fee-instrument-input" />
              )}
            </div>
            )}
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("fees.notes")}</Label>
              <Input value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="fee-notes-input" />
            </div>
            <Button className="w-full brand-bg text-white" onClick={submitEdit} data-testid="fee-edit-confirm">{t("common.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delFee} onOpenChange={() => setDelFee(null)}>
        <DialogContent className="card-bg border-app" data-testid="fee-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("fees.delete_title")}</DialogTitle>
            <DialogDescription className="t-secondary">{delFee?.member_name} — {delFee?.period_name} · {delFee ? eurCents(delFee.amount_due_cents) : ""}. {t("fees.delete_q")}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" className="border-app" onClick={() => setDelFee(null)} data-testid="fee-delete-cancel">{t("common.cancel")}</Button>
            <Button className="bg-rose-500 text-white hover:bg-rose-600" onClick={submitDelete} data-testid="fee-delete-confirm">{t("common.delete")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="card-bg border-app" data-testid="fee-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("fees.add_title")}</DialogTitle>
            <DialogDescription className="t-secondary">{periods.find((p) => p.id === periodId)?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("fees.member")}</Label>
              <Select value={addForm.member_id} onValueChange={(v) => setAddForm({ ...addForm, member_id: v })}>
                <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="fee-add-member-select"><SelectValue placeholder={t("usr.choose")} /></SelectTrigger>
                <SelectContent>
                  {membersWithoutFee.map((m) => <SelectItem key={m.id} value={m.id}>{m.prenom} {m.nom}</SelectItem>)}
                </SelectContent>
              </Select>
              {membersWithoutFee.length === 0 && <p className="text-xs t-secondary mt-1" data-testid="fee-add-none-left">{t("fees.all_have_fee")}</p>}
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("fees.amount_due")}</Label>
              <Input type="number" min="0" step="0.01" value={addForm.amount_due} onChange={(e) => setAddForm({ ...addForm, amount_due: e.target.value })} placeholder={periodAmount} className="mt-1 bg-transparent border-app" data-testid="fee-add-amount-input" />
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("fees.notes")}</Label>
              <Input value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="fee-add-notes-input" />
            </div>
            <Button className="w-full brand-bg text-white" onClick={submitAdd} disabled={!addForm.member_id} data-testid="fee-add-confirm">{t("fees.add_cotisation")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
